import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle,
  Loader2,
  CreditCard,
  TrendingUp,
  PiggyBank,
  BookOpen,
  Target,
  Sparkles,
  Share2,
  RotateCcw,
  Coffee,
  Wallet,
  Music,
  Users,
  ChevronDown,
  Trophy,
  Home,
  Rocket,
  Plane,
  TrendingDown,
  Coins,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ============= INVESTOR TYPE QUIZ DATA =============
const investorTypes = [
  {
    id: "builder",
    name: "The Strategic Builder",
    emoji: "",
    description: "Patient, research-focused, long-term thinker",
    color: "bg-blue-500",
    traits: ["Disciplined", "Research-oriented", "Patient", "Goal-focused"],
  },
  {
    id: "optimizer",
    name: "The Balanced Optimizer",
    emoji: "",
    description: "Smart risk-taker, diversifies well",
    color: "bg-emerald-500",
    traits: ["Analytical", "Balanced", "Strategic", "Adaptable"],
  },
  {
    id: "visionary",
    name: "The Bold Visionary",
    emoji: "",
    description: "High-risk tolerance, entrepreneurial mindset",
    color: "bg-purple-500",
    traits: ["Ambitious", "Risk-tolerant", "Innovative", "Confident"],
  },
  {
    id: "climber",
    name: "The Steady Climber",
    emoji: "",
    description: "Conservative, values security and consistency",
    color: "bg-amber-500",
    traits: ["Cautious", "Consistent", "Security-focused", "Methodical"],
  },
  {
    id: "learner",
    name: "The Future-Ready Learner",
    emoji: "",
    description: "Not investing yet, but eager to learn and grow",
    color: "bg-pink-500",
    traits: ["Curious", "Open-minded", "Growth-oriented", "Humble"],
  },
];

const investorQuizQuestions = [
  {
    id: "iq1",
    icon: Coffee,
    title: "The Daily Coffee Dilemma",
    question: "You're running late. Tim Hortons has a line, but you can make instant coffee at home.",
    options: [
      { value: "a", label: "Make it at home (save the $4)", scores: { builder: 4, climber: 3 } },
      { value: "b", label: "Grab Tim's once or twice a week as a treat", scores: { optimizer: 4, climber: 2 } },
      { value: "c", label: "Tim's every morning (it's my routine)", scores: { learner: 2, visionary: 2 } },
      { value: "d", label: "Starbucks premium drink because I deserve it", scores: { visionary: 2 } },
    ],
  },
  {
    id: "iq2",
    icon: Wallet,
    title: "Tax Refund: $1,200",
    question: "It's in your account right now. What happens next?",
    options: [
      { value: "a", label: "Transfer it all to savings/investments", scores: { builder: 4, climber: 3 } },
      { value: "b", label: "Invest $1,000, treat myself with $200", scores: { optimizer: 4, builder: 2 } },
      { value: "c", label: "Pay off my credit card or student loan first", scores: { climber: 4 } },
      { value: "d", label: "New laptop/trip/shopping spree!", scores: { visionary: 2, learner: 2 } },
    ],
  },
  {
    id: "iq3",
    icon: Music,
    title: "Spotify Just Increased to $18/Month",
    question: "You've been paying $11. What do you do?",
    options: [
      { value: "a", label: "Cancel and use the free version", scores: { builder: 4, climber: 3 } },
      { value: "b", label: "Switch to a family plan and split with friends", scores: { optimizer: 5 } },
      { value: "c", label: "Keep paying (I hate ads)", scores: { learner: 2 } },
      { value: "d", label: "What's $7? I barely check subscriptions", scores: { visionary: 2 } },
    ],
  },
  {
    id: "iq4",
    icon: Users,
    title: "Friday Night Plans",
    question: "Dinner and drinks will cost $60-80. You have $200 left for the week.",
    options: [
      { value: "a", label: "Suggest a potluck at someone's place", scores: { builder: 4, optimizer: 3 } },
      { value: "b", label: "Go, but set a strict $40 budget", scores: { optimizer: 4, climber: 3 } },
      { value: "c", label: "Go and have fun, worry about it Monday", scores: { visionary: 3, learner: 2 } },
      { value: "d", label: "Always down! Put it on the credit card", scores: { visionary: 2 } },
    ],
  },
  {
    id: "iq5",
    icon: Trophy,
    title: "You Just Won $100,000!",
    question: "No strings attached. What's your move?",
    options: [
      { value: "a", label: "80% into index funds, 20% for fun", scores: { builder: 5, optimizer: 3 } },
      { value: "b", label: "Down payment on a rental property", scores: { optimizer: 4, visionary: 3 } },
      { value: "c", label: "Start the business I've been dreaming about", scores: { visionary: 5 } },
      { value: "d", label: "High-interest savings (don't want to lose it)", scores: { climber: 4 } },
    ],
  },
  {
    id: "iq6",
    icon: Rocket,
    title: "Crypto FOMO",
    question: "Your roommate made $500 on crypto. You have $300 saved.",
    options: [
      { value: "a", label: "Research for a few days first", scores: { builder: 5, optimizer: 3 } },
      { value: "b", label: "Put in $50 just to test it out", scores: { optimizer: 4 } },
      { value: "c", label: "YOLO, invest all $300 right now", scores: { visionary: 3 } },
      { value: "d", label: "No thanks, sounds too risky", scores: { climber: 4, learner: 2 } },
    ],
  },
  {
    id: "iq7",
    icon: TrendingDown,
    title: "The $5,000 Crash",
    question: "Your $5,000 investment drops to $1,000 overnight. What's your move?",
    options: [
      { value: "a", label: "Buy more! It's on sale!", scores: { visionary: 4, optimizer: 2 } },
      { value: "b", label: "Hold steady (long-term mindset)", scores: { builder: 5, optimizer: 3 } },
      { value: "c", label: "Panic a little but do nothing", scores: { climber: 3, learner: 3 } },
      { value: "d", label: "Sell everything immediately", scores: { learner: 3 } },
    ],
  },
];

// ============= CATEGORY QUIZ DATA =============
const categoryQuizQuestions = [
  {
    id: "q1",
    title: "Debt Situation",
    question: "What best describes your current debt situation?",
    options: [
      { value: "A", label: "I have high-interest debt (credit cards, payday loans) that's overwhelming" },
      { value: "B", label: "I have manageable debt (student loans, car payment)" },
      { value: "C", label: "I'm debt-free but have little savings" },
      { value: "D", label: "I'm debt-free with emergency savings ready" }
    ]
  },
  {
    id: "q2",
    title: "Monthly Cash Flow",
    question: "At the end of each month, you typically have...",
    options: [
      { value: "A", label: "Nothing left or going into more debt" },
      { value: "B", label: "A little left ($0-$500) but it disappears" },
      { value: "C", label: "Regular savings ($500-$1,500) with no plan" },
      { value: "D", label: "Significant savings ($1,500+) ready to allocate" }
    ]
  },
  {
    id: "q3",
    title: "Primary Financial Goal",
    question: "What's your #1 financial priority right now?",
    options: [
      { value: "A", label: "Eliminate debt as fast as possible" },
      { value: "B", label: "Start investing but I'm a complete beginner" },
      { value: "C", label: "Grow my existing investments" },
      { value: "D", label: "Understand money and finance first" }
    ]
  },
  {
    id: "q4",
    title: "Financial Knowledge",
    question: "How would you rate your investing knowledge?",
    options: [
      { value: "A", label: "Beginner - I don't understand basic terms" },
      { value: "B", label: "Novice - I understand basics but haven't invested" },
      { value: "C", label: "Intermediate - I have investments and understand fundamentals" },
      { value: "D", label: "Advanced - I actively manage my portfolio" }
    ]
  },
  {
    id: "q6",
    title: "Time Horizon",
    question: "What's your timeline for financial goals?",
    options: [
      { value: "A", label: "Immediate (0-6 months) - urgent needs" },
      { value: "B", label: "Short-term (1-3 years) - saving for goals" },
      { value: "C", label: "Medium-term (3-10 years) - building wealth" },
      { value: "D", label: "Long-term (10+ years) - retirement" }
    ]
  },
  {
    id: "q7",
    title: "Income Stability",
    question: "How stable is your income and job situation?",
    options: [
      { value: "A", label: "Unstable - Freelance or at risk" },
      { value: "B", label: "Somewhat stable but no emergency fund" },
      { value: "C", label: "Stable with 3-6 months emergency fund" },
      { value: "D", label: "Very stable with 6+ months saved" }
    ]
  },
  {
    id: "q10",
    title: "Upcoming Expenses",
    question: "Do you have major expenses in the next 12 months?",
    options: [
      { value: "A", label: "Yes - Large expense ($10K+)" },
      { value: "B", label: "Yes - Medium goal ($3K-$10K)" },
      { value: "C", label: "Maybe - Should plan for something" },
      { value: "D", label: "No major expenses planned" }
    ]
  },
];

// Scoring Matrix for Category Quiz
const SCORING_MATRIX = {
  q1: {
    A: { debtFree: 15 },
    B: { debtFree: 8, planSave: 2 },
    C: { planSave: 10, learnFinance: 2 },
    D: { startInvesting: 10, buildWealth: 5 }
  },
  q2: {
    A: { debtFree: 12, planSave: 3 },
    B: { planSave: 10, debtFree: 3 },
    C: { startInvesting: 8, buildWealth: 4 },
    D: { buildWealth: 10, startInvesting: 5 }
  },
  q3: {
    A: { debtFree: 15 },
    B: { startInvesting: 15, learnFinance: 3 },
    C: { buildWealth: 15, startInvesting: 3 },
    D: { learnFinance: 15, planSave: 2 }
  },
  q4: {
    A: { learnFinance: 10, startInvesting: 2 },
    B: { startInvesting: 8, learnFinance: 5 },
    C: { buildWealth: 8, startInvesting: 5 },
    D: { buildWealth: 12, startInvesting: 3 }
  },
  q6: {
    A: { planSave: 12, debtFree: 5 },
    B: { planSave: 15 },
    C: { startInvesting: 10, buildWealth: 5 },
    D: { buildWealth: 15, startInvesting: 5 }
  },
  q7: {
    A: { planSave: 15, debtFree: 3 },
    B: { planSave: 12, debtFree: 3 },
    C: { startInvesting: 10, buildWealth: 5 },
    D: { buildWealth: 10, startInvesting: 5 }
  },
  q10: {
    A: { planSave: 20 },
    B: { planSave: 15 },
    C: { planSave: 5 },
    D: { startInvesting: 3, buildWealth: 3 }
  }
};

// Category Info
const CATEGORY_INFO = {
  debtFree: {
    name: "Get Debt-Free",
    path: "/debt-free",
    icon: CreditCard,
    color: "from-red-500 to-red-600",
    bgColor: "bg-red-50",
    textColor: "text-red-600",
    description: "Focus on eliminating debt and taking control"
  },
  startInvesting: {
    name: "Start Investing",
    path: "/start-investing",
    icon: TrendingUp,
    color: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-600",
    description: "Begin your investment journey with confidence"
  },
  buildWealth: {
    name: "Build Wealth",
    path: "/build-wealth",
    icon: PiggyBank,
    color: "from-amber-500 to-amber-600",
    bgColor: "bg-amber-50",
    textColor: "text-amber-600",
    description: "Optimize and grow your existing investments"
  },
  learnFinance: {
    name: "Learn Finance",
    path: "/learn-finance",
    icon: BookOpen,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-600",
    description: "Build your financial knowledge foundation"
  },
  planSave: {
    name: "Plan & Save",
    path: "/plan-save",
    icon: Target,
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-600",
    description: "Budget and save for your specific goals"
  }
};

// Calculate investor type result
const calculateInvestorResult = (answers) => {
  const scores = {
    builder: 0,
    optimizer: 0,
    visionary: 0,
    climber: 0,
    learner: 0,
  };

  Object.values(answers).forEach((answer) => {
    if (answer.scores) {
      Object.entries(answer.scores).forEach(([type, points]) => {
        scores[type] = (scores[type] || 0) + points;
      });
    }
  });

  const maxScore = Math.max(...Object.values(scores));
  const topTypeId = Object.entries(scores).find(([_, score]) => score === maxScore)?.[0];
  const topType = investorTypes.find(t => t.id === topTypeId) || investorTypes[4];

  return { type: topType, scores };
};

// Calculate category recommendation
const calculateCategoryResult = (answers) => {
  // Check Critical Overrides
  if (answers.q1 === 'A' && answers.q10 !== 'A') {
    return { category: 'debtFree', confidence: 'Very High', overrideUsed: true };
  }
  if (answers.q10 === 'A') {
    return { category: 'planSave', confidence: 'Very High', overrideUsed: true };
  }
  if (answers.q3 === 'D' && answers.q4 === 'A') {
    return { category: 'learnFinance', confidence: 'Very High', overrideUsed: true };
  }
  if (answers.q7 === 'A' && answers.q1 !== 'A') {
    return { category: 'planSave', confidence: 'High', overrideUsed: true };
  }

  // Calculate Scores
  const scores = { debtFree: 0, startInvesting: 0, buildWealth: 0, learnFinance: 0, planSave: 0 };
  
  Object.keys(answers).forEach(question => {
    const answer = answers[question];
    const points = SCORING_MATRIX[question]?.[answer] || {};
    Object.keys(points).forEach(category => {
      scores[category] += points[category];
    });
  });

  const sortedCategories = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const winner = sortedCategories[0];
  const runnerUp = sortedCategories[1];
  const pointDifference = winner[1] - runnerUp[1];

  let confidence;
  if (pointDifference >= 20) confidence = 'Very High';
  else if (pointDifference >= 10) confidence = 'High';
  else if (pointDifference >= 5) confidence = 'Medium';
  else confidence = 'Low';

  return { category: winner[0], confidence, scores, runnerUp: runnerUp[0], overrideUsed: false };
};

// ============= MAIN COMPONENT =============
const HomepageQuizFlow = ({ onComplete, mobileFreeMode = false, onRequireLogin }) => {
  // Flow stages: 'investor-intro' -> 'investor-quiz' -> 'investor-result' -> 'signin-prompt' -> 'category-quiz' -> 'final-result'
  const [flowStage, setFlowStage] = useState('investor-intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [investorAnswers, setInvestorAnswers] = useState({});
  const [categoryAnswers, setCategoryAnswers] = useState({});
  const [investorResult, setInvestorResult] = useState(null);
  const [categoryResult, setCategoryResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);
  
  // Check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    let token = localStorage.getItem("user_token") || localStorage.getItem("token");
    let userData = localStorage.getItem("user_data") || localStorage.getItem("user");
    
    if (!token || !userData) {
      token = sessionStorage.getItem("user_token");
      userData = sessionStorage.getItem("user_data");
    }
    
    const loggedIn = !!(token && userData);
    setIsLoggedIn(loggedIn);
    
    // Check if user just logged in and has pending quiz results
    if (loggedIn) {
      const pendingAnswers = localStorage.getItem("pendingInvestorQuizAnswers");
      const pendingResult = localStorage.getItem("pendingInvestorQuizResult");
      
      if (pendingAnswers && pendingResult) {
        try {
          const answers = JSON.parse(pendingAnswers);
          const result = JSON.parse(pendingResult);
          
          // Restore the quiz state
          setInvestorAnswers(answers);
          setInvestorResult(result);
          setFlowStage('investor-result');
          
          // Clear the pending data
          localStorage.removeItem("pendingInvestorQuizAnswers");
          localStorage.removeItem("pendingInvestorQuizResult");
        } catch (e) {
          console.error("Failed to restore quiz state:", e);
        }
      }
    }
  }, []);

  // Investor Quiz handlers
  const handleInvestorAnswer = (questionId, option) => {
    setInvestorAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const handleInvestorNext = () => {
    if (currentQuestion < investorQuizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Calculate investor result
      const result = calculateInvestorResult(investorAnswers);
      setInvestorResult(result);
      localStorage.setItem("investorQuizCompleted", result.type.name);
      
      // Check login status directly at this moment (not relying on state)
      const token = localStorage.getItem("user_token") || localStorage.getItem("token") || sessionStorage.getItem("user_token");
      const userData = localStorage.getItem("user_data") || localStorage.getItem("user") || sessionStorage.getItem("user_data");
      const isCurrentlyLoggedIn = !!(token && userData);
      
      // If mobile free mode and not logged in, save answers and show sign-in prompt
      if (mobileFreeMode && !isCurrentlyLoggedIn) {
        // Save quiz state so we can restore it after login
        localStorage.setItem("pendingInvestorQuizAnswers", JSON.stringify(investorAnswers));
        localStorage.setItem("pendingInvestorQuizResult", JSON.stringify(result));
        setFlowStage('signin-prompt');
      } else {
        setFlowStage('investor-result');
      }
    }
  };

  // Category Quiz handlers
  const handleCategoryAnswer = (questionId, value) => {
    setCategoryAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCategoryNext = async () => {
    if (currentQuestion < categoryQuizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setIsLoading(true);
      // Calculate category result
      const result = calculateCategoryResult(categoryAnswers);
      
      // Try to get AI reasoning
      try {
        const response = await fetch(`${API_URL}/api/quiz/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: categoryAnswers, algorithmResult: result })
        });
        if (response.ok) {
          const data = await response.json();
          result.aiReasoning = data;
        }
      } catch (error) {
        console.error('AI analysis error:', error);
      }
      
      // Save to localStorage so dashboard can access results
      localStorage.setItem("recommendedCategory", result.category);
      localStorage.setItem("onboardingComplete", "true");
      
      // Save to database for persistence across sessions
      try {
        const token = localStorage.getItem("user_token") || localStorage.getItem("token") || sessionStorage.getItem("user_token");
        const userData = localStorage.getItem("user_data") || localStorage.getItem("user") || sessionStorage.getItem("user_data");
        
        if (token && userData) {
          const user = JSON.parse(userData);
          const userId = user.email || user.id;
          
          await fetch(`${API_URL}/api/progress/onboarding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              investor_type: investorResult?.type?.name || localStorage.getItem("investorQuizCompleted") || "",
              recommended_category: result.category,
              investor_answers: investorAnswers,
              category_answers: categoryAnswers
            })
          });
          console.log("Onboarding progress saved to database");
        }
      } catch (saveError) {
        console.error("Error saving onboarding progress:", saveError);
      }
      
      setCategoryResult(result);
      setIsLoading(false);
      setFlowStage('final-result');
    }
  };

  const handleStartCategoryQuiz = () => {
    setCurrentQuestion(0);
    setFlowStage('category-quiz');
  };

  const handleRetake = () => {
    setInvestorAnswers({});
    setCategoryAnswers({});
    setInvestorResult(null);
    setCategoryResult(null);
    setCurrentQuestion(0);
    setFlowStage('investor-intro');
    // Clear localStorage so user can get new results
    localStorage.removeItem("investorQuizCompleted");
    localStorage.removeItem("recommendedCategory");
    localStorage.removeItem("onboardingComplete");
  };

  const handleShare = async () => {
    const shareText = `I'm "${investorResult?.type?.name}" ${investorResult?.type?.emoji}! My recommended path is ${CATEGORY_INFO[categoryResult?.category]?.name}. Take the quiz:`;
    const shareUrl = `${window.location.origin}/`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Financial Path - Compoundr", text: shareText, url: shareUrl });
        toast.success("Thanks for sharing!");
        return;
      } catch (err) { /* fallback */ }
    }
    
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Could not copy link");
    }
  };

  // Calculate progress
  const getProgress = () => {
    if (flowStage === 'investor-quiz') {
      return ((currentQuestion + 1) / investorQuizQuestions.length) * 50;
    }
    if (flowStage === 'category-quiz') {
      return 50 + ((currentQuestion + 1) / categoryQuizQuestions.length) * 50;
    }
    if (flowStage === 'investor-result') return 50;
    if (flowStage === 'final-result') return 100;
    return 0;
  };

  // ============= RENDER STAGES =============
  
  // Stage 1: Investor Quiz Intro
  if (flowStage === 'investor-intro') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-center mb-4">
          {/* GET STARTED HERE - Bold text with arrow */}
          <p className="text-navy-700 text-lg font-black uppercase tracking-wide mb-2 flex items-center justify-center gap-2">
            Get Started Here
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </p>
          
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-3">
            What Kind of Investor Are You?
          </h2>
          <p className="text-base text-slate-700 font-semibold mb-1">
            Start with our 2-minute personality quiz to discover your investing style.
          </p>
          <p className="text-sm text-emerald-600 font-bold">
            Free • No sign up required
          </p>
        </div>

        {/* Investor Types Preview */}
        <div className="grid grid-cols-5 gap-2 mb-5">
          {investorTypes.map((type) => (
            <div key={type.id} className="bg-white/80 backdrop-blur rounded-xl p-2 text-center">
              <div className={`w-8 h-8 ${type.color} rounded-full mx-auto mb-1`}></div>
              <p className="text-[10px] font-medium text-slate-700 truncate">{type.name.split(' ')[1]}</p>
            </div>
          ))}
        </div>

        <Button
          onClick={() => { setCurrentQuestion(0); setFlowStage('investor-quiz'); }}
          className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-lg font-semibold rounded-xl py-6 shadow-lg"
          data-testid="start-investor-quiz-btn"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Discover My Investor Type
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </motion.div>
    );
  }

  // Stage 2: Investor Quiz Questions
  if (flowStage === 'investor-quiz') {
    const currentQ = investorQuizQuestions[currentQuestion];
    const currentAnswer = investorAnswers[currentQ?.id];
    const progress = ((currentQuestion + 1) / investorQuizQuestions.length) * 100;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-2xl mx-auto"
      >
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">
              Quiz 1 of 2 • Question {currentQuestion + 1}/{investorQuizQuestions.length}
            </span>
            <span className="text-sm text-emerald-600 font-medium">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="border-0 shadow-lg" data-testid="investor-quiz-card">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <currentQ.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <CardTitle className="font-heading text-lg">{currentQ.title}</CardTitle>
                </div>
                <p className="text-slate-600">{currentQ.question}</p>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={currentAnswer?.value || ""}
                  onValueChange={(value) => {
                    const option = currentQ.options.find((o) => o.value === value);
                    handleInvestorAnswer(currentQ.id, option);
                  }}
                >
                  <div className="space-y-3">
                    {currentQ.options.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          currentAnswer?.value === option.value
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 hover:border-emerald-300"
                        }`}
                      >
                        <RadioGroupItem value={option.value} />
                        <span className="text-slate-700 text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </RadioGroup>

                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => currentQuestion === 0 ? setFlowStage('investor-intro') : setCurrentQuestion(s => s - 1)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleInvestorNext}
                    disabled={!currentAnswer}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="investor-quiz-next-btn"
                  >
                    {currentQuestion === investorQuizQuestions.length - 1 ? 'See Results' : 'Next'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  }

  // Stage 3: Investor Result + Prompt for Category Quiz
  if (flowStage === 'investor-result' && investorResult) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className={`w-20 h-20 ${investorResult.type.color} rounded-full flex items-center justify-center mx-auto mb-4`}
          >
            <span className="text-4xl">{investorResult.type.emoji}</span>
          </motion.div>
          <Badge className="bg-emerald-100 text-emerald-700 mb-2">Your Investor Type</Badge>
          <h2 className="text-2xl font-heading font-bold text-navy-700 mb-1">
            {investorResult.type.name}
          </h2>
          <p className="text-slate-600 mb-3">{investorResult.type.description}</p>
          <div className="flex flex-wrap justify-center gap-1 mb-4">
            {investorResult.type.traits?.map((trait) => (
              <Badge key={trait} variant="secondary" className="text-xs">{trait}</Badge>
            ))}
          </div>
        </div>

        {/* CTA for Category Quiz */}
        <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0 text-white mb-4">
          <CardContent className="p-6 text-center">
            <span className="text-3xl mb-2 block">🎯</span>
            <h3 className="text-xl font-heading font-bold mb-2">
              Step 2: Category Recommendation
            </h3>
            <p className="text-purple-100 text-sm mb-1">Your starting point</p>
            <p className="text-purple-200 text-xs mb-4">
              Find which financial category matches your current situation!
            </p>
            <Button
              onClick={handleStartCategoryQuiz}
              className="bg-white text-purple-700 hover:bg-purple-50 font-semibold px-8 py-5"
              data-testid="start-category-quiz-btn"
            >
              Take Category Recommendation Quiz
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <div className="flex justify-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')} 
            className="text-slate-500"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <Button variant="ghost" onClick={handleRetake} className="text-slate-500">
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake Quiz
          </Button>
        </div>
      </motion.div>
    );
  }

  // Stage 3.5: Sign-in Prompt (for mobile free mode)
  if (flowStage === 'signin-prompt' && investorResult) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </motion.div>
          <Badge className="bg-emerald-100 text-emerald-700 mb-2">Quiz Complete!</Badge>
          <h2 className="text-2xl font-heading font-bold text-navy-700 mb-2">
            Your Results Are Ready! 🎉
          </h2>
          <p className="text-slate-600 mb-4">
            Sign in to unlock your personalized investor type and get your recommended starting path.
          </p>
        </div>

        {/* Teaser Card */}
        <Card className="mb-4 border-2 border-dashed border-slate-300 bg-slate-50">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl blur-sm">🎯</span>
            </div>
            <p className="text-slate-500 font-medium">Your Investor Type</p>
            <p className="text-slate-400 text-sm">Sign in to reveal</p>
          </CardContent>
        </Card>

        {/* Sign In CTA */}
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 text-white mb-4">
          <CardContent className="p-6 text-center">
            <span className="text-3xl mb-2 block">🔓</span>
            <h3 className="text-xl font-heading font-bold mb-2">
              Unlock Your Full Results
            </h3>
            <p className="text-emerald-100 text-sm mb-4">
              Create a free account to see your investor personality, get personalized recommendations, and access all tools!
            </p>
            <Button
              onClick={() => {
                if (onRequireLogin) {
                  onRequireLogin();
                }
              }}
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8 py-5 w-full"
              data-testid="signin-to-see-results-btn"
            >
              Sign In to See Results
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <div className="flex justify-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')} 
            className="text-slate-500"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <Button variant="ghost" onClick={handleRetake} className="text-slate-500">
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake Quiz
          </Button>
        </div>
      </motion.div>
    );
  }

  // Stage 4: Category Quiz Questions
  if (flowStage === 'category-quiz') {
    const currentQ = categoryQuizQuestions[currentQuestion];
    const currentAnswer = categoryAnswers[currentQ?.id];
    const progress = ((currentQuestion + 1) / categoryQuizQuestions.length) * 100;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-2xl mx-auto"
      >
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">
              Category Recommendation • Question {currentQuestion + 1}/{categoryQuizQuestions.length}
            </span>
            <span className="text-sm text-purple-600 font-medium">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2 [&>div]:bg-purple-500" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="border-0 shadow-lg" data-testid="category-quiz-card">
              <CardContent className="p-6">
                <Badge className="mb-4 bg-purple-100 text-purple-600">{currentQ.title}</Badge>
                <h3 className="text-xl font-heading font-bold text-navy-700 mb-6">
                  {currentQ.question}
                </h3>

                <div className="space-y-3">
                  {currentQ.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleCategoryAnswer(currentQ.id, option.value)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        currentAnswer === option.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                          currentAnswer === option.value
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-slate-300'
                        }`}>
                          {currentAnswer === option.value && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          currentAnswer === option.value ? 'text-purple-700 font-medium' : 'text-slate-700'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => currentQuestion === 0 ? setFlowStage('investor-result') : setCurrentQuestion(s => s - 1)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleCategoryNext}
                    disabled={!currentAnswer}
                    className="bg-purple-500 hover:bg-purple-600"
                    data-testid="category-quiz-next-btn"
                  >
                    {currentQuestion === categoryQuizQuestions.length - 1 ? 'See Results' : 'Next'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-heading font-bold text-navy-700 mb-2">
          Analyzing Your Responses...
        </h2>
        <p className="text-slate-600">Our AI is crafting personalized recommendations</p>
      </div>
    );
  }

  // Stage 5: Final Combined Result
  if (flowStage === 'final-result' && categoryResult && investorResult) {
    const categoryInfo = CATEGORY_INFO[categoryResult.category];
    const CategoryIcon = categoryInfo?.icon || Target;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        {/* Success Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 mb-2">Quiz Complete!</Badge>
          <h2 className="text-2xl font-heading font-bold text-navy-700">
            Your Personalized Financial Path
          </h2>
        </div>

        {/* Investor Type Summary */}
        <Card className="mb-4 border border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-14 h-14 ${investorResult.type.color} rounded-full flex items-center justify-center shrink-0`}>
              <span className="text-2xl">{investorResult.type.emoji}</span>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Your Investor Type</p>
              <p className="font-heading font-bold text-navy-700">{investorResult.type.name}</p>
              <p className="text-sm text-slate-600">{investorResult.type.description}</p>
            </div>
          </CardContent>
        </Card>

        {/* Recommended Category */}
        <Card className="mb-4 overflow-hidden border-2 border-emerald-200">
          <div className={`bg-gradient-to-r ${categoryInfo.color} p-5 text-white`}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <CategoryIcon className="w-7 h-7" />
              </div>
              <div>
                <Badge className="bg-white/20 text-white border-0 mb-1 text-xs">
                  {categoryResult.confidence} Confidence Match
                </Badge>
                <h3 className="text-xl font-heading font-bold">{categoryInfo.name}</h3>
                <p className="text-white/80 text-sm">{categoryInfo.description}</p>
              </div>
            </div>
          </div>
          <CardContent className="p-5">
            {categoryResult.aiReasoning?.reasoning && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  <span className="font-semibold text-sm text-navy-700">Why This Path?</span>
                </div>
                <p className="text-slate-600 text-sm">{categoryResult.aiReasoning.reasoning}</p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={categoryInfo.path} className="flex-1">
                <Button className={`w-full bg-gradient-to-r ${categoryInfo.color} hover:opacity-90 text-white py-5`}>
                  Go to {categoryInfo.name}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/tools" className="flex-1">
                <Button variant="outline" className="w-full py-5">
                  Browse All Tools
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Go to Your Dashboard - Prominent CTA after quiz completion */}
        <Link to="/dashboard" className="block mb-4">
          <Card className="bg-gradient-to-r from-navy-700 to-navy-800 border-0 hover:opacity-95 transition-opacity">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white text-lg">Go to Your Dashboard</h4>
                    <p className="text-slate-300 text-sm">Your personalized financial journey awaits</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Wealthsimple $25 Banner - Always visible */}
        <div 
          onClick={() => setWealthsimpleModalOpen(true)}
          className="block mb-4 cursor-pointer"
        >
          <Card className="bg-[#00C853] border-0 hover:opacity-95 transition-opacity">
            <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shrink-0">
                  <span className="text-[#00C853] text-xl font-bold">W</span>
                </div>
                <span className="font-heading font-bold text-sm sm:text-base">
                  Get free $25 towards your Wealthsimple TFSA account
                </span>
              </div>
              <Button className="bg-white text-[#00C853] hover:bg-white/90 font-bold px-6 rounded-full whitespace-nowrap">
                Claim Free $25 →
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Wealthsimple Steps Modal */}
        <WealthsimpleStepsModal 
          open={wealthsimpleModalOpen} 
          onOpenChange={setWealthsimpleModalOpen} 
        />

        {/* Free Starter Guides - Prominent for Build Wealth & Start Investing */}
        <Card className="mb-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-bold text-navy-700 mb-1">
                  📚 View Our Free Starter Guides
                </h4>
                <p className="text-slate-600 text-sm mb-3">
                  {categoryResult.category === 'startInvesting' 
                    ? "New to investing? Our beginner guides will walk you through opening your first TFSA, understanding ETFs, and making your first investment."
                    : categoryResult.category === 'buildWealth'
                    ? "Ready to grow your money? Learn about compound interest, portfolio building, and long-term wealth strategies in our free guides."
                    : categoryResult.category === 'learnFinance'
                    ? "Build your financial knowledge with our comprehensive guides covering everything from budgeting basics to investment fundamentals."
                    : categoryResult.category === 'planSave'
                    ? "Learn effective budgeting strategies and saving techniques with our practical guides designed for students."
                    : "Get started with our free educational guides covering TFSAs, RRSPs, ETFs, and more."}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className="bg-indigo-100 text-indigo-700 text-xs">TFSA Guide</Badge>
                  <Badge className="bg-purple-100 text-purple-700 text-xs">ETF Basics</Badge>
                  <Badge className="bg-blue-100 text-blue-700 text-xs">Budgeting 101</Badge>
                  {(categoryResult.category === 'startInvesting' || categoryResult.category === 'buildWealth') && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">Investment Strategies</Badge>
                  )}
                </div>
                <Link to="/products">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <BookOpen className="w-4 h-4 mr-2" />
                    View Free Guides
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share & Retake */}
        <div className="flex justify-center gap-3">
          <Button onClick={handleShare} variant="outline" className="text-emerald-600 border-emerald-200">
            <Share2 className="w-4 h-4 mr-2" />
            Share Results
          </Button>
          <Button variant="ghost" onClick={handleRetake} className="text-slate-500">
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake
          </Button>
        </div>
      </motion.div>
    );
  }

  return null;
};

export default HomepageQuizFlow;
